/**
 * ChatChart — Mini chart renderer for AI chat responses
 *
 * Renders compact Recharts visualizations inline in chat bubbles.
 * Supports: pie (budget allocation), bar (channel ROAS), line (trends).
 *
 * Issue: YDMA-179 §3.1
 */

import React from 'react';
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import type { ChartSpec } from '../../services/agentService';

// YDM brand colours live in one module so a second chart cannot fork them.
import { YDM_CHART_COLORS } from '../charts/chartPalette';

/**
 * Platform brand color mapping for channel breakdown charts.
 * When chart data labels contain platform names, use their brand colors
 * for instant visual recognition.
 */
const PLATFORM_COLORS: Record<string, string> = {
  // Meta / Facebook / Instagram
  'facebook': '#1877F2',
  'fb': '#1877F2',
  'meta': '#0668E1',
  'instagram': '#E4405F',
  'ig': '#E4405F',
  // Google
  'google': '#4285F4',
  'google search': '#4285F4',
  'google ads': '#4285F4',
  'youtube': '#FF0000',
  'yt': '#FF0000',
  // TikTok
  'tiktok': '#000000',
  'tt': '#000000',
  // LINE
  'line': '#06C755',
  'line oa': '#06C755',
  // Twitter / X
  'twitter': '#1DA1F2',
  'x': '#000000',
  // LinkedIn
  'linkedin': '#0A66C2',
  // Other common
  'display': '#9333EA',
  'programmatic': '#6B7280',
  'email': '#EC4899',
  'sms': '#10B981',
};

/**
 * Keys that only earn their brand colour when they are the whole label. A lone
 * "x" is a multiplication sign, a size or a collaboration ("Meta x Nike") far
 * more often than it is Twitter/X, and one letter matches almost any label.
 */
const EXACT_MATCH_ONLY = new Set(['x']);

/**
 * Reduces a label to space-separated words: lowercased, every run of anything
 * that is not a letter or digit collapsed to one space, and padded with a space
 * at each end so a whole-word search is a plain substring test.
 *
 * Splitting on punctuation rather than using \b keeps "facebook_ads" and
 * "Facebook - Retargeting" matchable, and lets a platform be found inside a
 * Thai label ("แคมเปญ Facebook") the same way.
 */
const toWords = (value: string): string => ` ${value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;

/**
 * The keywords a longer label may be searched for, tokenised once and ordered
 * longest first so a two-word platform beats the one-word key inside it
 * ("google search" before "google"). Array.sort is stable, so keys of equal
 * length keep the declaration order above.
 */
const MATCHABLE_KEYWORDS: ReadonlyArray<readonly [words: string, color: string]> =
  Object.entries(PLATFORM_COLORS)
    .filter(([keyword]) => !EXACT_MATCH_ONLY.has(keyword))
    .sort(([a], [b]) => b.length - a.length)
    .map(([keyword, color]) => [toWords(keyword), color] as const);

/**
 * Returns brand color for a data label if it names a known platform,
 * otherwise falls back to the YDM palette by index.
 *
 * A keyword has to appear as a whole word: these colours also name platforms in
 * the channel table beside the chart, so painting "Analytics" YouTube red
 * (anal-YT-ics) or "Online sales" LINE green (on-LINE) would be a wrong claim
 * about which channel a slice belongs to, not just an odd colour.
 */
const getColorForLabel = (label: string, fallbackIndex: number): string => {
  const normalized = label.toLowerCase().trim();
  // Check exact match first
  if (PLATFORM_COLORS[normalized]) return PLATFORM_COLORS[normalized];
  // Then any platform named as a whole word within a longer label
  const words = toWords(normalized);
  for (const [keyword, color] of MATCHABLE_KEYWORDS) {
    if (words.includes(keyword)) return color;
  }
  return YDM_CHART_COLORS[fallbackIndex % YDM_CHART_COLORS.length];
};

// ============================================================
// CHART SUB-RENDERERS
// ============================================================

const renderPieChart = (spec: ChartSpec) => {
  const data = spec.data;
  return (
    <ResponsiveContainer width="100%" height={180}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          outerRadius={65}
          innerRadius={30}
          paddingAngle={2}
        >
          {data.map((entry, idx) => (
            <Cell key={idx} fill={getColorForLabel(entry.label, idx)} />
          ))}
        </Pie>
        <Tooltip
          formatter={(val) => {
            const n = Number(val);
            return spec.valueFormat === 'currency'
              ? `฿${Math.round(n).toLocaleString()}`
              : String(n);
          }}
          contentStyle={{ fontSize: '12px', borderRadius: '4px', border: '1px solid #E5E7EB' }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

const renderBarChart = (spec: ChartSpec) => {
  const data = spec.data;
  return (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10 }}
          interval={0}
          angle={-15}
          textAnchor="end"
          height={36}
        />
        <YAxis tick={{ fontSize: 10 }} />
        <Tooltip
          formatter={(val) => {
            const n = Number(val);
            return spec.valueFormat === 'currency'
              ? `฿${Math.round(n).toLocaleString()}`
              : String(n);
          }}
          contentStyle={{ fontSize: '12px', borderRadius: '4px', border: '1px solid #E5E7EB' }}
          cursor={{ fill: 'rgba(247, 148, 29, 0.08)' }}
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {data.map((entry, idx) => (
            <Cell key={idx} fill={getColorForLabel(entry.label, idx)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const renderLineChart = (spec: ChartSpec) => {
  const data = spec.data;
  return (
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10 }}
          interval={Math.floor(data.length / 6)}
        />
        <YAxis tick={{ fontSize: 10 }} />
        <Tooltip
          formatter={(val) => {
            const n = Number(val);
            return spec.valueFormat === 'currency'
              ? `฿${Math.round(n).toLocaleString()}`
              : String(n);
          }}
          contentStyle={{ fontSize: '12px', borderRadius: '4px', border: '1px solid #E5E7EB' }}
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke="#F7941D"
          strokeWidth={2}
          dot={{ r: 3, fill: '#F7941D' }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const ChatChart: React.FC<{ spec: ChartSpec }> = ({ spec }) => {
  if (!spec || !spec.data || spec.data.length === 0) return null;

  return (
    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
      {spec.title && (
        <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
          {spec.type === 'pie' && '🥧'}
          {spec.type === 'bar' && '📊'}
          {spec.type === 'line' && '📈'}
          {spec.title}
        </p>
      )}
      {spec.type === 'pie' && renderPieChart(spec)}
      {spec.type === 'bar' && renderBarChart(spec)}
      {spec.type === 'line' && renderLineChart(spec)}
    </div>
  );
};

export default ChatChart;
